import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PerformanceAnalyticsSection = () => {
  const [timeRange, setTimeRange] = useState('30days');

  const improvementData = [
    { date: '11/01', score: 65 },
    { date: '11/08', score: 68 },
    { date: '11/15', score: 72 },
    { date: '11/22', score: 78 },
    { date: '11/28', score: 82 }
  ];

  const skillsData = [
    { skill: 'Algorithms', score: 85 },
    { skill: 'System Design', score: 72 },
    { skill: 'Data Structures', score: 88 },
    { skill: 'Problem Solving', score: 80 },
    { skill: 'Communication', score: 75 }
  ];

  const stats = [
    {
      label: 'Total Interviews',
      value: '24',
      change: '+8',
      icon: 'Mic',
      color: 'var(--color-primary)'
    },
    {
      label: 'Average Score',
      value: '82%',
      change: '+12%',
      icon: 'TrendingUp',
      color: 'var(--color-success)'
    },
    {
      label: 'Practice Hours',
      value: '18.5',
      change: '+5.2',
      icon: 'Clock',
      color: 'var(--color-accent)'
    },
    {
      label: 'Improvement Rate',
      value: '26%',
      change: '+4%',
      icon: 'BarChart3',
      color: 'var(--color-secondary)'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">
                Performance Analytics
              </h2>
              <p className="text-sm text-muted-foreground">
                Track your interview improvement over time
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {['7days', '30days', '90days']?.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`
                  px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ease-out
                  ${timeRange === range 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                {range === '7days' ? '7 Days' : range === '30days' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats?.map((stat, index) => (
            <div
              key={index}
              className="bg-muted/30 border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-150 ease-out"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat?.color}15` }}>
                  <Icon name={stat?.icon} size={20} color={stat?.color} />
                </div>
                <span className="text-xs font-medium text-success bg-success/10 px-2 py-1 rounded">
                  {stat?.change}
                </span>
              </div>
              <div className="text-2xl font-heading font-bold text-foreground mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-muted-foreground">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-6">
          <h3 className="text-sm font-medium text-foreground mb-4">
            Score Improvement Trend
          </h3>
          <div className="w-full h-64" aria-label="Interview score improvement line chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={improvementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-primary)', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-muted/30 border border-border rounded-lg p-6">
          <h3 className="text-sm font-medium text-foreground mb-4">
            Skills Assessment
          </h3>
          <div className="w-full h-64" aria-label="Skills assessment bar chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={skillsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="skill" 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="score" 
                  fill="var(--color-secondary)" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              <h3 className="text-sm font-medium text-foreground">Strengths</h3>
            </div>
            <ul className="space-y-2">
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-success rounded-full" />
                Data Structures & Algorithms
              </li>
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-success rounded-full" />
                Problem-Solving Approach
              </li>
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-success rounded-full" />
                Code Quality & Best Practices
              </li>
            </ul>
          </div>

          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
              <h3 className="text-sm font-medium text-foreground">Areas to Improve</h3>
            </div>
            <ul className="space-y-2">
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-warning rounded-full" />
                System Design Concepts
              </li>
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-warning rounded-full" />
                Communication Clarity
              </li>
              <li className="text-sm text-muted-foreground flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-warning rounded-full" />
                Time Management
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalyticsSection;